import { z } from 'zod';

/* ============================================================
   Prospect — incoming data about the lead
   ============================================================ */

export const ProspectSegmentSchema = z.enum(['saas', 'service', 'ecommerce', 'enterprise', 'sme']);
export type ProspectSegment = z.infer<typeof ProspectSegmentSchema>;

export const ProspectStageSchema = z.enum([
  'pre-revenue',
  'early',
  'growth',
  'mature',
  'enterprise',
]);

export const ProspectSizeSchema = z.enum(['solo', 'micro', 'sme', 'mid-market', 'enterprise']);

/**
 * Brief screenshot — uploaded as visual context for the vision LLM.
 * Stored in R2 bucket `sab-proposal-assets/brief/<uuid>.<ext>`. The
 * auto-extract step reads bytes, base64-encodes, and forwards as image
 * content block to Claude Haiku 4.5 (primary) or Workers AI Llama 3.2
 * Vision (fallback). After analyze, kept for audit; lifecycle rule
 * deletes folder `brief/` after 30 days if not migrated to a proposal.
 */
export const BriefImageSchema = z.object({
  key: z.string().min(1).max(300),
  contentType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
  name: z.string().max(200).optional(),
  width: z.number().int().positive().max(8_000).optional(),
  height: z.number().int().positive().max(8_000).optional(),
  bytes: z.number().int().positive().max(10_000_000).optional(),
});
export type BriefImage = z.infer<typeof BriefImageSchema>;

export const ProspectIntakeSchema = z.object({
  companyName: z.string().trim().min(1, 'Nama perusahaan wajib diisi').max(200),
  /** OPTIONAL — auto-extracted from screenshot kalau ada, else manual fill. */
  contactName: z.string().trim().max(120).optional(),
  /** OPTIONAL — sama, opsional. */
  contactRole: z.string().trim().max(120).optional(),
  websiteUrl: z.string().trim().url('URL website tidak valid').max(500).optional(),
  industry: z.string().trim().max(120).optional(),
  geography: z.string().trim().max(120).default('Indonesia'),
  language: z.enum(['id', 'en']).default('id'),
  /** DEPRECATED — pricing now auto-decided by tools (Stage 4 country-aware).
   * Kept optional for backwards compatibility with proposals saved before
   * the form rebuild. New proposals do NOT surface this field in the UI. */
  budgetHintIDR: z.number().int().positive().max(10_000_000_000).optional(),
  timelineWeeks: z.number().int().positive().max(104).optional(),
  rawMessage: z.string().trim().max(8_000).optional(),
  notes: z.string().trim().max(4_000).optional(),
  /** Up to 10 brief screenshots — surfaced to the vision LLM for
   * auto-extract (pre-analyze) AND for analyze context. */
  briefImages: z.array(BriefImageSchema).max(10).default([]),
});
export type ProspectIntake = z.infer<typeof ProspectIntakeSchema>;

/* ============================================================
   Website analysis output
   ============================================================ */

export const ProspectAnalysisSchema = z.object({
  segment: ProspectSegmentSchema,
  stage: ProspectStageSchema,
  size: ProspectSizeSchema,
  industry: z.string().max(120),
  positioning: z.string().max(400),
  productsServices: z.array(z.string().max(200)).max(20),
  targetCustomer: z.string().max(400),
  uniqueValueProp: z.string().max(400),
  painPoints: z.array(z.string().max(300)).max(8),
  competitors: z.array(z.string().max(120)).max(10),
  brandTone: z.string().max(200),
  brandColorsHex: z
    .array(z.string().regex(/^#[0-9a-fA-F]{6}$/))
    .max(6)
    .default([]),
  designMaturity: z.enum(['poor', 'average', 'good', 'excellent']),
  marketingMaturity: z.enum(['none', 'basic', 'intermediate', 'advanced']),
  estimatedRevenueIDR: z
    .object({
      min: z.number().int().nonnegative(),
      max: z.number().int().positive(),
    })
    .optional(),
  pitchAngle: z.string().max(400),
  recommendedPackage: z.enum(['startup', 'growth', 'scale', 'enterprise']),
  recommendedPriceIDR: z.number().int().positive(),
  rationale: z.string().max(1_000),
});
export type ProspectAnalysis = z.infer<typeof ProspectAnalysisSchema>;

/* ============================================================
   Slides — 25-slide deck contract
   ============================================================ */

export const SlideTypeSchema = z.enum([
  'cover',
  'opportunity',
  'problem',
  'why-matters',
  'sab-intro',
  'process',
  'case-study',
  'services',
  'difference',
  'approach',
  'strategy',
  'expected-results',
  'pricing',
  'why-sab',
  'timeline',
  'next-steps',
  'contact',
  'appendix',
  'custom',
]);
export type SlideType = z.infer<typeof SlideTypeSchema>;

export const MetricSchema = z.object({
  label: z.string().max(120),
  value: z.string().max(80),
  delta: z.string().max(40).optional(),
  tone: z.enum(['neutral', 'positive', 'negative']).default('neutral'),
});

export const SlideContentSchema = z.object({
  eyebrow: z.string().max(120).optional(),
  headline: z.string().max(280),
  subheadline: z.string().max(500).optional(),
  bullets: z.array(z.string().max(300)).max(8).default([]),
  body: z.string().max(2_000).optional(),
  metrics: z.array(MetricSchema).max(6).default([]),
  caseStudy: z
    .object({
      client: z.string().max(120),
      industry: z.string().max(120),
      challenge: z.string().max(500),
      solution: z.string().max(500),
      result: z.string().max(500),
      metrics: z.array(MetricSchema).max(4).default([]),
    })
    .optional(),
  pricing: z
    .object({
      tier: z.enum(['startup', 'growth', 'scale', 'enterprise']),
      monthlyIDR: z.number().int().positive(),
      commitmentMonths: z.number().int().min(1).max(36),
      includes: z.array(z.string().max(200)).max(15),
      expectedLeadsMin: z.number().int().nonnegative().optional(),
      expectedLeadsMax: z.number().int().positive().optional(),
    })
    .optional(),
  cta: z
    .object({
      label: z.string().max(80),
      href: z.string().max(300).optional(),
    })
    .optional(),
  imageUrl: z.string().max(500).optional(),
  imageAlt: z.string().max(300).optional(),
});
export type SlideContent = z.infer<typeof SlideContentSchema>;

export const SlideSchema = z.object({
  id: z.string().uuid(),
  index: z.number().int().min(0).max(99),
  type: SlideTypeSchema,
  content: SlideContentSchema,
});
export type Slide = z.infer<typeof SlideSchema>;

/* ============================================================
   Proposal — top-level document
   ============================================================ */

export const ProposalStatusSchema = z.enum(['draft', 'published', 'archived']);
export type ProposalStatus = z.infer<typeof ProposalStatusSchema>;

export const ProposalSchema = z.object({
  id: z.string().uuid(),
  slug: z
    .string()
    .min(3)
    .max(120)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Slug invalid'),
  status: ProposalStatusSchema,
  prospect: ProspectIntakeSchema,
  analysis: ProspectAnalysisSchema,
  slides: z.array(SlideSchema).min(1).max(40),
  theme: z
    .object({
      accentHex: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .default('#FF6B35'),
      heroImageUrl: z.string().max(500).optional(),
    })
    .default({ accentHex: '#FF6B35' }),
  createdBy: z.string().email().max(200),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  publishedAt: z.string().datetime().optional(),
  version: z.number().int().nonnegative().default(1),
});
export type Proposal = z.infer<typeof ProposalSchema>;

/* ============================================================
   API I/O contracts (admin)
   ============================================================ */

export const GenerateRequestSchema = z.object({
  intake: ProspectIntakeSchema,
  forceAnalysisRefresh: z.boolean().default(false),
  /** Model id from `MODELS` registry. Falls back to env DEFAULT_MODEL if omitted. */
  modelId: z.string().min(1).max(80).optional(),
});
export type GenerateRequest = z.infer<typeof GenerateRequestSchema>;

export const GenerateResponseSchema = z.object({
  analysis: ProspectAnalysisSchema,
  slides: z.array(SlideSchema),
  diagnostics: z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    model: z.string(),
    latencyMs: z.number().int().nonnegative(),
    cached: z.boolean(),
  }),
});
export type GenerateResponse = z.infer<typeof GenerateResponseSchema>;

export const PublishRequestSchema = z.object({
  slug: z
    .string()
    .min(3)
    .max(120)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/),
});
export type PublishRequest = z.infer<typeof PublishRequestSchema>;

export const ApiErrorSchema = z.object({
  error: z.string(),
  code: z.string(),
  details: z.unknown().optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;
